import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';

import {
  CdkOverlayOrigin,
  Overlay, OverlayConfig,
  OverlayRef
} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {InputBoolean} from '../core/convert';

@Component({
  selector: 'jd-drawer',
  templateUrl: './drawer.component.html',
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .ant-drawer {
      position: fixed;
      top: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
      transition: transform 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
      justify-content: center;
    }

    .ant-drawer > * {
      transition: transform 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
    }

    .ant-drawer-content-wrapper {
      position: absolute;
    }

    .ant-drawer-left .ant-drawer-content-wrapper,
    .ant-drawer-right .ant-drawer-content-wrapper,
    .ant-drawer-left .ant-drawer-content,
    .ant-drawer-right .ant-drawer-content {
      height: 100%;
    }

    .ant-drawer-top .ant-drawer-content-wrapper,
    .ant-drawer-bottom .ant-drawer-content-wrapper,
    .ant-drawer-bottom .ant-drawer-content,
    .ant-drawer-top .ant-drawer-content {
      width: 100%;
    }

    .ant-drawer-left.ant-drawer-open .ant-drawer-content-wrapper {
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    }

    .ant-drawer-right .ant-drawer-content-wrapper {
      right: 0;
    }

    .ant-drawer-right.ant-drawer-open .ant-drawer-content-wrapper {
      box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
    }

    .ant-drawer.ant-drawer-open > * {
      pointer-events: auto;
    }

    .ant-drawer.ant-drawer-open .ant-drawer-mask {
      opacity: 0.3;
    }

    .ant-drawer-title {
      margin: 0;
      font-size: 16px;
      line-height: 22px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.85);
    }

    .ant-drawer-content {
      position: relative;
      background-color: #fff;
      border: 0;
      background-clip: padding-box;
      z-index: 1;
    }

    .ant-drawer-close {
      cursor: pointer;
      border: 0;
      background: transparent;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 10;
      font-weight: 700;
      line-height: 1;
      text-decoration: none;
      transition: color 0.3s;
      color: rgba(0, 0, 0, 0.45);
      outline: 0;
      padding: 0;
    }

    .ant-drawer-close-x {
      display: block;
      font-style: normal;
      text-align: center;
      text-transform: none;
      text-rendering: auto;
      width: 56px;
      height: 56px;
      line-height: 56px;
      font-size: 16px;
    }

    .ant-drawer-close-x:before {
      content: "\\e633";
      display: block;
      font-family: "anticon" !important;
    }

    .ant-drawer-close:focus,
    .ant-drawer-close:hover {
      color: #444;
      text-decoration: none;
    }

    .ant-drawer-header {
      padding: 16px 24px;
      border-radius: 4px 4px 0 0;
      background: #fff;
      color: rgba(0, 0, 0, 0.65);
      border-bottom: 1px solid #e8e8e8;
    }

    .ant-drawer-body {
      padding: 24px;
      font-size: 14px;
      line-height: 1.5;
      word-wrap: break-word;
    }

    .ant-drawer-mask {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      opacity: 0;
      background-color: rgba(0, 0, 0, 0.65);
      height: 100%;
      transition: opacity 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
      filter: alpha(opacity=50);
    }

    .ant-drawer-open {
      overflow: hidden;
    }

    .ant-drawer-open-content {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    body, html {
      padding: 0;
      margin: 0;
      height: 100%;
      width: 100%;
    }
  `]
})
export class JdDrawerComponent implements OnInit, OnDestroy {
  origin: CdkOverlayOrigin;
  overlayRef: OverlayRef;
  portal: TemplatePortal;
  isOpen = false;
  @Input() @InputBoolean() jdClosable = true;
  @Input() @InputBoolean() jdMaskClosable = true;
  @Input() @InputBoolean() jdMask = true;
  @Input() jdTitle: string | TemplateRef<{}>;
  @Input() jdMaskStyle: object = {};
  @Input() jdBodyStyle: object = {};
  @Input() jdWrapClassName: string;
  @Input() jdWidth: number | string = 0;
  @Input() jdHeight: number | string = 0;
  @Input() jdPlacement: 'left' | 'right' | 'top' | 'bottom' = 'right';
  @Input() jdZIndex = 1000;
  @Input() jdOffsetX = 0;
  @Input() jdOffsetY = 0;
  @Output() jdOnClose = new EventEmitter<MouseEvent>();
  @ViewChild('drawerTemplate') drawerTemplate: TemplateRef<{}>;
  private _stateSave: any = {};
  private _layout$ = new Subject<{
    xs: boolean;
    sm: boolean;
    md: boolean;
    lg: boolean;
    xl: boolean;
    xxl: boolean;
  }>();

  get transform(): string {
    const _height = document.body.offsetHeight;
    const _width = document.body.offsetWidth;
    let tempWidth = '';
    if (this.jdPlacement === 'left' || this.jdPlacement === 'right') {
      if (/%/.test(this.width)) {
        tempWidth = '100%';
        if (this.width === '100%') {
          this.jdClosable = true;
        }
      } else if (_width - parseInt(this.width, 10) <= 0 || this.width === '100%') {
        tempWidth = _width + 'px';
        this.jdWidth = tempWidth;
        this.jdClosable = true;
      } else {
        tempWidth = this.width;
      }
    }
    if (this.jdPlacement === 'left') {
      return this.isOpen ? `translateX(${this.jdOffsetX}px)` : `translateX(-${tempWidth})`;
    } else if (this.jdPlacement === 'right') {
      return this.isOpen ? `translateX(-${this.jdOffsetX}px)` : `translateX(${tempWidth})`;
    } else if (this.jdPlacement === 'top') {
      return this.isOpen ? `translateY(${this.jdOffsetY}px)` : `translateY(-${this.height})`;
    } else if (this.jdPlacement === 'bottom') {
      return this.isOpen ? `translateY(${_height - parseInt(this.height, 10) + this.jdOffsetY}px)` :
        `translateY(${_height}px)`;
    }
  }

  get width(): string {
    if (this.jdPlacement === 'top' || this.jdPlacement === 'bottom') {
      return this.jdWidth ? (typeof this.jdWidth === 'number' ? `${this.jdWidth}px` : this.jdWidth) :
        '100%';
    }
    return typeof this.jdWidth === 'number' && this.jdWidth ? `${this.jdWidth}px` : this.jdWidth ?
      this.jdWidth + '' : '256px';
  }

  get height(): string {
    if (this.jdPlacement === 'top' || this.jdPlacement === 'bottom') {
      return typeof this.jdHeight === 'number' && this.jdHeight ? `${this.jdHeight}px` : '256px';
    } else {
      return this.jdHeight ? (typeof this.jdHeight === 'number' ? `${this.jdHeight}px` : this.jdHeight) :
        '100%';
    }
  }

  @Input()
  set jdVisible(value: boolean) {
    this.isOpen = value;
    this.updateOverlayStyle();
  }

  get jdVisible(): boolean {
    return this.isOpen;
  }

  private _stateSaveF() {
    this._stateSave['oldValue'] = {
      width: this.jdWidth,
      jdClosable: this.jdClosable
    };
    this._stateSave['newValue'] = {};
  }

  isNonEmptyString(value: {}): boolean {
    return typeof value === 'string' && value !== '';
  }

  isTemplateRef(value: {}): boolean {
    return value instanceof TemplateRef;
  }

  constructor(
    private renderer: Renderer2,
    private overlay: Overlay,
    private elementRef: ElementRef,
    private _bm: BreakpointObserver,
    // private jd: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef) {
    this._listenLayout();
  }

  private _listenLayout() {
    this._bm
      .observe([Breakpoints.Small])
      .pipe(debounceTime(5))
      .subscribe(() => {
        const xs = this._bm.isMatched('(max-width: 479.98px)'),
          sm = this._bm.isMatched('(min-width: 480px) and (max-width: 767.98px)'),
          md = this._bm.isMatched('(min-width: 768px) and (max-width: 991.98px)'),
          lg = this._bm.isMatched('(min-width: 992px) and (max-width: 1199.98px)'),
          xl = this._bm.isMatched('(min-width: 1200px) and (max-width: 1599.98px)'),
          xxl = this._bm.isMatched('(min-width: 1600px)');
        this._layout$.next({xs, sm, md, lg, xl, xxl});
      });
  }

  ngOnInit(): void {
    this.origin = new CdkOverlayOrigin(this.elementRef);
    this.attachOverlay();
    this.updateOverlayStyle();
    this._stateSaveF();
    this._layout$.subscribe(value => {
      if (value.xs || value.sm) {
        this.jdWidth = '100%';
        if (this.jdPlacement === 'left' || this.jdPlacement === 'right') {
          this.jdClosable = true;
        }
      } else if (value.md || value.xl || value.xxl || value.lg) {
        this.jdWidth = this._stateSave['oldValue'].width;
        this.jdClosable = this._stateSave['oldValue'].jdClosable;
      }
    });
  }

  ngOnDestroy(): void {
    this.disposeOverlay();
  }

  attachOverlay(): void {
    if (!this.overlayRef) {
      this.portal = new TemplatePortal(this.drawerTemplate, this.viewContainerRef);
      this.overlayRef = this.overlay.create(this.getOverlayConfig());
    }

    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      this.overlayRef.attach(this.portal);
    }
  }

  disposeOverlay(): void {
    this.overlayRef.dispose();
    this.overlayRef = null;
  }

  getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
  }

  updateOverlayStyle(): void {
    if (this.overlayRef && this.overlayRef.overlayElement) {
      this.renderer.setStyle(this.overlayRef.overlayElement, 'pointer-events',
        this.isOpen ? 'auto' : 'none');
    }
  }

  close(e?: MouseEvent): void {
    this.jdOnClose.emit(e);
  }

  maskClick(e: MouseEvent): void {
    if (this.jdMaskClosable && this.jdMask) {
      this.close(e);
    }
  }

}
